import React, { useEffect, useMemo, useRef, useState } from "react";
import { pdf } from "@react-pdf/renderer";
import { useProposal } from "../../context/ProposalContext";
import { useRates } from "../../context/RatesContext";
import { calculateEstimate, computeTotals, estimatedVotersFor, formatCurrency, ELECTION_TYPES } from "../../lib/costEngine";
import { saveProposal, emailProposal, sharedPdfUrl } from "../../lib/api";
import ProposalDocument from "../ProposalDocument";
import { SMARTBALLOT_LOGO } from "../../assets/smartBallotLogo";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { ArrowLeft, FileText, CheckCircle2, Download, Feather, Loader2, Link2, Check } from "lucide-react";
import { toast } from "sonner";

export default function Step4Proposal({ onBack, onReset }) {
  const { state } = useProposal();
  const { rates } = useRates();
  const { event, lead } = state;
  const uploadedDoc = state.document;
  const estimate = useMemo(() => {
    const items = state.items && state.items.length ? state.items : calculateEstimate(event, rates).items;
    const totals = computeTotals(items, rates.taxRate || 0);
    return { items, ...totals, estimatedVoters: estimatedVotersFor(event.membershipSize, event.voterTurnout) };
  }, [state.items, event, rates]);
  const typeLabel = ELECTION_TYPES[event.electionType]?.label || "Custom";

  const [producing, setProducing] = useState(true);
  const [done, setDone] = useState(false);
  const [shareToken, setShareToken] = useState("");
  const [copied, setCopied] = useState(false);
  const produced = useRef(false);

  const buildPdfBlob = async () =>
    pdf(<ProposalDocument event={event} estimate={estimate} lead={lead} />).toBlob();

  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const downloadBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(event.eventTitle || "SmartBallot-Proposal").replace(/\s+/g, "-")}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const produce = async () => {
    // Build the PDF, but never let a rendering hiccup block saving/emailing.
    let blob = null;
    try {
      blob = await buildPdfBlob();
    } catch (e) {
      console.error("PDF build failed:", e && e.stack ? e.stack : e);
    }

    // 1) Email the proposal to the recipient (only possible if we have a PDF).
    let sentAt = null;
    if (blob) {
      try {
        const res = await emailProposal(blob, event, lead, estimate.total);
        if (res.emailed) {
          sentAt = new Date().toISOString();
          toast.success(`Proposal emailed to ${lead.email}.`);
        } else {
          const why = res.reason ? ` (${res.reason})` : "";
          toast.error(`Couldn't email the proposal right now${why}. It's saved — download it below or resend from the dashboard.`);
        }
      } catch (e) {
        console.error("email send failed", e);
      }
    }

    // 2) Record a copy of the proposal for the admin CRM (always, so it is never lost).
    let pdfBase64 = "";
    if (blob) {
      try {
        pdfBase64 = await blobToBase64(blob);
      } catch (e) {
        console.error("pdf encode failed", e);
      }
    }
    const payload = {
      eventTitle: event.eventTitle,
      membershipSize: Number(event.membershipSize),
      electionType: event.electionType,
      governingRegulation: event.governingRegulation || null,
      electronicVoting: event.electronicVoting || null,
      internationalVoters: Number(event.internationalVoters) || 0,
      pollingLocations: Number(event.pollingLocations) || 1,
      startDate: event.startDate || null,
      endDate: event.endDate || null,
      voterTurnout: event.voterTurnout ? Number(event.voterTurnout) : null,
      location: event.location,
      items: estimate.items,
      fixedSubtotal: estimate.fixedSubtotal,
      variableSubtotal: estimate.variableSubtotal,
      subtotal: estimate.subtotal,
      tax: estimate.tax,
      total: estimate.total,
      lead,
      documentKey: uploadedDoc?.key || null,
      documentName: uploadedDoc?.name || null,
      sentAt,
      preparedBy: event.preparedBy || null,
      pdfBase64,
    };
    try {
      const saved = await saveProposal(payload);
      if (saved?.shareToken) setShareToken(saved.shareToken);
    } catch (e) {
      console.error("save failed", e);
    }
    try {
      const leads = JSON.parse(localStorage.getItem("onefeather_leads") || "[]");
      leads.push({ ...lead, total: estimate.total, at: new Date().toISOString() });
      localStorage.setItem("onefeather_leads", JSON.stringify(leads));
    } catch (e) {
      console.error("local lead store failed", e);
    }

    // 3) Download a copy for the user (if the PDF built) and finish.
    if (blob) {
      downloadBlob(blob);
      setDone(true);
    } else {
      toast.error("Your proposal was saved, but the PDF could not be generated. Please try 'Download Again'.");
    }
    setProducing(false);
  };

  // Produce the proposal once when the step is reached (CRM info is already complete here).
  useEffect(() => {
    if (produced.current) return;
    produced.current = true;
    produce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadAgain = async () => {
    try {
      const blob = await buildPdfBlob();
      downloadBlob(blob);
    } catch (e) {
      console.error("PDF build failed (download again):", e && e.stack ? e.stack : e);
      toast.error("Could not generate PDF.");
    }
  };

  const copyShareLink = async () => {
    if (!shareToken) return;
    const url = sharedPdfUrl(shareToken);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Shareable link copied to clipboard.");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      // Clipboard API unavailable (e.g. insecure context) — fall back to manual copy.
      window.prompt("Copy this shareable link:", url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="space-y-6" data-testid="proposal-step">
      <div>
        <h2 className="font-display font-700 text-2xl text-foreground">Your Proposal</h2>
        <p className="text-muted-foreground text-sm mt-1">
          {producing
            ? "Generating your branded, client-ready proposal…"
            : "Your proposal has been generated and downloaded."}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-start">
        {/* Branded cover preview — mirrors the printed B&W PDF */}
        <Card data-testid="pdf-preview" className="overflow-hidden border-border">
          <div className="bg-white text-foreground p-6 aspect-[3/4] flex flex-col">
            <img src={SMARTBALLOT_LOGO} alt="SmartBallot" className="h-10 object-contain" />
            <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-4">SmartBallot</p>
            <p className="font-display font-700 text-2xl leading-tight mt-1 text-foreground">
              {typeLabel} Voting Event Services Proposal
            </p>
            <div className="mt-4">
              <p className="font-display font-700 text-base text-foreground">
                {lead.firstNation || lead.organization || "your community"}
              </p>
              {lead.name && (
                <p className="text-sm text-muted-foreground">
                  {lead.name}{lead.jobTitle ? `, ${lead.jobTitle}` : ""}
                </p>
              )}
              {event.eventTitle && (
                <p className="text-xs text-muted-foreground mt-1">{event.eventTitle}</p>
              )}
            </div>
            <div className="mt-auto pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground">Total Estimated Budget</p>
              <p className="font-display font-700 text-lg text-foreground">{formatCurrency(estimate.total)} CAD</p>
              <p className="font-display font-700 text-base text-foreground mt-2">smartballot.app</p>
            </div>
          </div>
        </Card>

        {/* Status + actions */}
        <div className="space-y-3">
          {producing ? (
            <Card className="bg-accent/40 border-accent" data-testid="producing-state">
              <CardContent className="p-5 flex items-center gap-3 text-primary">
                <Loader2 className="h-5 w-5 animate-spin" />
                <p className="font-display font-600">Preparing your proposal…</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-green-50 border-green-200" data-testid="success-screen">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-green-700 mb-2">
                  <CheckCircle2 className="h-5 w-5" />
                  <p className="font-display font-700">Proposal delivered</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  Your branded proposal PDF has been downloaded and sent. Thank you!
                </p>
                {shareToken && (
                  <div className="mt-3 pt-3 border-t border-green-200" data-testid="share-link-section">
                    <p className="text-xs text-muted-foreground mb-2">
                      Share this proposal with colleagues (no login required):
                    </p>
                    <div className="flex items-center gap-3">
                      {/* Mini preview thumbnail — visual confirmation of what's being shared */}
                      <div
                        data-testid="share-thumbnail"
                        className="shrink-0 w-14 h-[74px] rounded-md border border-green-200 bg-white p-1.5 flex flex-col shadow-sm"
                        title="Proposal preview"
                      >
                        <img src={SMARTBALLOT_LOGO} alt="" className="h-3 object-contain object-left" />
                        <p className="text-[4.5px] leading-[6px] font-display font-700 text-foreground mt-1 line-clamp-3">
                          {typeLabel} Voting Event Services Proposal
                        </p>
                        <p className="text-[4px] text-muted-foreground mt-auto truncate">
                          {lead.firstNation || lead.organization || "Community"}
                        </p>
                        <p className="text-[5px] font-display font-700 text-foreground">
                          {formatCurrency(estimate.total)}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid="copy-share-link-btn"
                        onClick={copyShareLink}
                        className={`gap-2 transition-colors ${copied ? "bg-green-600 text-white border-green-600 copy-ring" : "bg-white"}`}
                      >
                        {copied ? (
                          <Check className="h-3.5 w-3.5 copy-pop" />
                        ) : (
                          <Link2 className="h-3.5 w-3.5" />
                        )}
                        {copied ? "Link copied!" : "Copy shareable link"}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="bg-accent/40 border-accent">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 text-primary mb-3">
                <FileText className="h-4 w-4" />
                <p className="font-display font-600 text-foreground">Included in your PDF</p>
              </div>
              <ul className="text-sm text-muted-foreground space-y-2">
                {["Branded cover page", "Cover letter & scope", "Itemized pricing table", "Terms & conditions"].map((x) => (
                  <li key={x} className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-primary" /> {x}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button
              variant="outline"
              data-testid="download-again-btn"
              onClick={downloadAgain}
              disabled={producing}
              className="flex-1 gap-2"
            >
              <Download className="h-4 w-4" /> Download Again
            </Button>
            <Button data-testid="new-proposal-btn" onClick={onReset} disabled={producing} className="flex-1 gap-2">
              <Feather className="h-4 w-4" /> New Proposal
            </Button>
          </div>
        </div>
      </div>

      <div className="flex justify-between pt-2">
        <Button variant="outline" data-testid="back-to-recipient-btn" onClick={onBack} disabled={producing} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
      </div>
    </div>
  );
}
